import { observable, computed, makeObservable } from 'mobx';
import { createModelSchema, primitive, object } from 'serializr';
import { optional } from '@vromo/models';
import { conditionDefinitions } from '@vromo/dashboard/src/policies/Condition';

type ConditionDescription = {
    condition: string;
    value: string | number | boolean;
};

const timeProperties = [
    {
        unit: 'Hours',
        value: 3600000
    },
    {
        unit: 'Minutes',
        value: 60000
    },
    {
        unit: 'Seconds',
        value: 1000
    }
];

export function formatTime(ms: number): string {
    const timeProp = timeProperties.find((tp) => ms % tp.value === 0);
    if (timeProp) {
        return `${ms / timeProp.value} ${timeProp.unit}`;
    } else {
        return `${ms} Milliseconds`;
    }
}

export const stackingLabels = ['pickupToPickup', 'dropoffToDropoff', 'dropoffToPickup'];

export class StackingRules {
    pickupToPickup?: number | null = null;
    dropoffToDropoff?: number | null = null;
    dropoffToPickup?: number | null = null;

    constructor() {
        makeObservable(this, {
            pickupToPickup: observable,
            dropoffToDropoff: observable,
            dropoffToPickup: observable,
            fieldDescriptions: computed
        });
    }

    get fieldDescriptions(): ConditionDescription[] {
        const conditionDescriptions = [] as ConditionDescription[];

        stackingLabels.forEach((item) => {
            const { max } = conditionDefinitions.get(item)!;
            const value = this[item as keyof typeof this] as number | boolean;
            const isValid = !!value && value !== 0 && showCondition(value, max as number);
            if (isValid)
                conditionDescriptions.push({
                    condition: item,
                    value: value
                });
        });

        return conditionDescriptions;
    }
}

createModelSchema(StackingRules, {
    pickupToPickup: primitive(),
    dropoffToDropoff: primitive(),
    dropoffToPickup: primitive()
});

export const conditionLabels = [
    'range',
    'afterCreation',
    'beforeDeadline',
    'pickupToDropoffLessThan',
    'pickupToDropoffGreaterThan',
    'maxJobs',
    'checkControlledContents',
    'orderValueLessThan',
    'orderValueGreaterThan',
    'paymentMethod'
];

export class PolicyRule {
    id: number | null = null;
    created: number | null = null;

    team: number | null = null;
    mode: string | null = null;
    includeSubgroups = false;

    stacking: StackingRules | null = null;
    range: number | null = null;
    afterCreation: number | null = null;
    beforeDeadline: number | null = null;
    maxJobs: number | null = null;
    pickupToDropoffGreaterThan: number | null = null;
    pickupToDropoffLessThan: number | null = null;
    checkControlledContents = false;
    orderValueLessThan: number | null = null;
    orderValueGreaterThan: number | null = null;
    paymentMethod: string | null = null;

    constructor() {
        makeObservable(this, {
            team: observable,
            mode: observable,
            range: observable,
            afterCreation: observable,
            beforeDeadline: observable,
            maxJobs: observable,
            stacking: observable,
            pickupToDropoffLessThan: observable,
            pickupToDropoffGreaterThan: observable,
            checkControlledContents: observable,
            orderValueLessThan: observable,
            orderValueGreaterThan: observable,
            fieldDescriptions: computed,
            includeSubgroups: observable,
            paymentMethod: observable
        });
    }

    get fieldDescriptions(): ConditionDescription[] {
        const conditionDescriptions = [] as ConditionDescription[];

        conditionLabels.forEach((item) => {
            const { max } = conditionDefinitions.get(item)!;
            const value = this[item as keyof typeof this] as number | boolean;
            const isValid = !!value && value !== 0 && showCondition(value, max as number);
            if (isValid)
                conditionDescriptions.push({
                    condition: item,
                    value: value
                });
        });

        return conditionDescriptions;
    }
}

createModelSchema(PolicyRule, {
    id: optional(primitive()),
    created: optional(primitive()),

    mode: optional(primitive()),

    range: optional(primitive()),
    afterCreation: optional(primitive()),
    beforeDeadline: optional(primitive()),
    maxJobs: optional(primitive()),

    team: optional(primitive()),
    stacking: optional(object(StackingRules)),
    pickupToDropoffLessThan: optional(primitive()),
    pickupToDropoffGreaterThan: optional(primitive()),
    checkControlledContents: optional(primitive()),
    includeSubgroups: optional(primitive()),
    orderValueLessThan: optional(primitive()),
    orderValueGreaterThan: optional(primitive()),
    paymentMethod: optional(primitive())
});

export const defaultRule = (): Partial<PolicyRule> => {
    return {
        mode: 'offer',
        includeSubgroups: false,
        ...conditionLabels.reduce((accum, curr) => {
            return { ...accum, [curr]: conditionDefinitions.get(curr)?.default };
        }, {})
    };
};

const showCondition = (value: number | boolean | string, max: number) => (max ? value < max : true);
